const DB_NAME = 'rl_cache_db';
const STORE_NAME = 'cache';
const DB_VERSION = 1;
const SWEEP_INTERVAL_MS = 5 * 60 * 1000;

const IN_FLIGHT = new Map();
export const CACHE_MISS = Symbol('CACHE_MISS');

let dbPromise = null;
const openDb = () => dbPromise ??= new Promise((resolve, reject) => {
  if (typeof indexedDB === 'undefined') {
    reject(new Error('IndexedDB not available'));
    return;
  }
  const request = indexedDB.open(DB_NAME, DB_VERSION);
  request.onupgradeneeded = () => {
    if (!request.result.objectStoreNames.contains(STORE_NAME)) {
      request.result.createObjectStore(STORE_NAME);
    }
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
});

const idbOp = (mode, op) => openDb().then((db) => new Promise((resolve, reject) => {
  const tx = db.transaction(STORE_NAME, mode);
  const req = op(tx.objectStore(STORE_NAME));
  tx.oncomplete = () => resolve(req?.result);
  tx.onerror = () => reject(tx.error);
})).catch(() => undefined);

let lastSweepAt = 0;
let sweepScheduled = false;

const sweepExpiredIfDue = () => {
  const now = Date.now();
  if (now - lastSweepAt < SWEEP_INTERVAL_MS) return;
  lastSweepAt = now;

  idbOp('readwrite', (store) => {
    const req = store.openCursor();
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) return;
      if (!cursor.value || now > cursor.value.expiresAt) cursor.delete();
      cursor.continue();
    };
    return req;
  });
};

const scheduleSweepIfNeeded = () => {
  if (sweepScheduled || typeof window.requestIdleCallback === 'undefined') return;
  sweepScheduled = true;
  window.requestIdleCallback(() => {
    sweepScheduled = false;
    sweepExpiredIfDue();
  }, { timeout: 60000 });
};

export const lsRead = async (key) => {
  const entry = await idbOp('readonly', (store) => store.get(key));
  if (!entry) return CACHE_MISS;
  if (Date.now() > entry.expiresAt) {
    idbOp('readwrite', (store) => store.delete(key));
    return CACHE_MISS;
  }
  return entry.data;
};

const lsWrite = (key, data, ttlMs) => {
  if (data == null) return;
  scheduleSweepIfNeeded();
  return idbOp('readwrite', (store) => store.put({ data, expiresAt: Date.now() + ttlMs }, key));
};

export const cache = (key, factory, ttlMs) => {
  if (IN_FLIGHT.has(key)) {
    return IN_FLIGHT.get(key);
  }

  const promise = (async () => {
    const cached = await lsRead(key);
    if (cached !== CACHE_MISS) {
      return { data: cached, status: 200 };
    }

    const res = await factory();
    if (res.status >= 200 && res.status < 300) {
      await lsWrite(key, res.data, ttlMs);
    }
    return res;
  })();

  IN_FLIGHT.set(key, promise);
  promise.finally(() => IN_FLIGHT.delete(key));
  return promise;
};

export const invalidateByPrefix = async (prefix) => {
  await idbOp('readwrite', (store) => (prefix ? store.delete(IDBKeyRange.bound(prefix, prefix + '￿')) : store.clear()));
  for (const key of IN_FLIGHT.keys()) {
    if (key.startsWith(prefix)) IN_FLIGHT.delete(key);
  }
};

export const clearAll = async () => {
  await idbOp('readwrite', (store) => store.clear());
  IN_FLIGHT.clear();
};
